import * as React from "react"

type ToolcraftTooltipPlacement =
  | "above"
  | "below"
  | "start"
  | "end"
  | "top"
  | "bottom"
  | "left"
  | "right"

export interface ToolcraftTooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  delayDuration?: number
  placement?: ToolcraftTooltipPlacement
}

/**
 * Kept so callers and the app root do not have to change. There is nothing to
 * provide any more, because the tooltip no longer holds state.
 */
export function ToolcraftTooltipProvider({
  children,
}: {
  children: React.ReactNode
  delayDuration?: number
}) {
  return <>{children}</>
}

const POSITION: Record<ToolcraftTooltipPlacement, string> = {
  above: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
  top: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
  below: "top-full left-1/2 -translate-x-1/2 mt-1.5",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
  start: "right-full top-1/2 -translate-y-1/2 mr-1.5",
  left: "right-full top-1/2 -translate-y-1/2 mr-1.5",
  end: "left-full top-1/2 -translate-y-1/2 ml-1.5",
  right: "left-full top-1/2 -translate-y-1/2 ml-1.5",
}

/**
 * A hover hint, drawn in CSS.
 *
 * This used to be Radix Tooltip. Radix positions its content with Popper, which
 * measures the trigger through composed refs and a resize observer, and in the
 * toolbar that measurement fed itself: moving the mouse across the buttons
 * opened a tooltip whose positioning re-rendered the toolbar, which re-rendered
 * the tooltip, until React gave up with "Maximum update depth exceeded" and the
 * whole editor went down. A stack that deep is hard to read and impossible to
 * act on, and the feature it was buying is a label on hover.
 *
 * So there is no portal, no popper, no ref, and no state here. The tip is a
 * sibling span revealed by group-hover and group-focus-within, which cannot
 * loop because nothing measures anything. It is positioned relative to the
 * trigger, so a tip near a window edge can clip; that is the trade, and it is
 * a better one than an editor that crashes.
 */
export function ToolcraftTooltip({
  children,
  content,
  placement = "above",
}: ToolcraftTooltipProps) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        role="tooltip"
        className={[
          "pointer-events-none absolute z-50 whitespace-nowrap rounded-md bg-bg-3 px-2 py-1",
          "text-[12px] leading-none text-fg opacity-0 shadow-lg transition-opacity duration-100",
          "group-hover:opacity-100 group-focus-within:opacity-100",
          POSITION[placement],
        ].join(" ")}
      >
        {content}
      </span>
    </span>
  )
}
